/* global window, FB */

const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

const loadExternalScript = (src, callback) => {
  try {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      if (callback) callback();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => callback && callback();
    script.onerror = error => callback && callback(error);
    document.head.appendChild(script);
  } catch (error) {
    if (callback) callback(error);
  }
};

export const loadFacebookSdk = () => {
  if (window.FB) {
    return Promise.resolve(window.FB);
  }

  return new Promise((resolve, reject) => {
    loadExternalScript(FACEBOOK_SDK_SRC, err => {
      if (err) {
        reject(err);
        return;
      }

      const checkInterval = setInterval(() => {
        if (window.FB) {
          clearInterval(checkInterval);
          resolve(window.FB);
        }
      }, 50);
    });
  });
};

export const initializeFacebook = (appId, apiVersion) => {
  if (!window.FB) {
    return;
  }

  window.FB.init({
    appId,
    autoLogAppEvents: true,
    xfbml: false,
    version: apiVersion,
  });
};

export const setupFacebookSdk = async (appId, apiVersion) => {
  if (!appId || !apiVersion) {
    throw new Error('WhatsApp Embedded Signup configuration is missing');
  }
  await loadFacebookSdk();
  initializeFacebook(appId, apiVersion);
};

export const initWhatsAppEmbeddedSignup = configId =>
  new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('Facebook SDK not loaded'));
      return;
    }

    window.FB.login(
      response => {
        if (response && response.authResponse && response.authResponse.code) {
          resolve(response.authResponse.code);
        } else {
          reject(new Error('Authorization code not received from Facebook'));
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        // Extras según la documentación de Meta para permitir
        // onboarding de números creados desde la app de WhatsApp Business.
        extras: JSON.stringify({
          featureType: 'whatsapp_business_app_onboarding',
          sessionInfoVersion: '3',
          version: 'v3',
          features: [
            { name: 'marketing_messages_lite' },
            { name: 'app_only_install' },
          ],
        }),
      }
    );
  });

export const createMessageHandler = onData => event => {
  try {
    const { origin, data } = event;

    // eslint-disable-next-line no-console
    console.log('[WA_EMBEDDED_SIGNUP][listener] MessageEvent recibido', {
      origin,
      data,
      dataType: typeof data,
    });

    if (!origin || !origin.includes('facebook.com')) {
      // eslint-disable-next-line no-console
      console.log(
        '[WA_EMBEDDED_SIGNUP][listener] Ignorando evento por origin no permitido',
        origin
      );
      return;
    }

    let payload = data;
    if (typeof data === 'string') {
      // eslint-disable-next-line no-console
      console.log(
        '[WA_EMBEDDED_SIGNUP][listener] data es string, intentando parsear',
        data
      );

      // Formato 1 (documentado): objeto JSON directo con { type: 'WA_EMBEDDED_SIGNUP', ... }
      // Formato 2 (observado en algunos ejemplos): 'WA_EMBEDDED_SIGNUP::{"event":"FINISH",...}'
      if (data.startsWith('WA_EMBEDDED_SIGNUP::')) {
        const jsonPart = data.substring('WA_EMBEDDED_SIGNUP::'.length);
        try {
          const parsed = JSON.parse(jsonPart);
          payload = {
            type: 'WA_EMBEDDED_SIGNUP',
            ...parsed,
          };

          // eslint-disable-next-line no-console
          console.log(
            '[WA_EMBEDDED_SIGNUP][listener] Payload parseado desde string con prefijo',
            payload
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(
            '[WA_EMBEDDED_SIGNUP][listener] Error parseando payload con prefijo WA_EMBEDDED_SIGNUP::',
            e
          );
          return;
        }
      } else {
        try {
          payload = JSON.parse(data);
          // eslint-disable-next-line no-console
          console.log(
            '[WA_EMBEDDED_SIGNUP][listener] Payload parseado desde JSON string',
            payload
          );
        } catch (e) {
          // eslint-disable-next-line no-console
          console.log(
            '[WA_EMBEDDED_SIGNUP][listener] data string no es JSON válido, se ignora'
          );
          // si no es JSON, lo ignoramos
          return;
        }
      }
    } else {
      // eslint-disable-next-line no-console
      console.log(
        '[WA_EMBEDDED_SIGNUP][listener] data no es string, usando objeto directamente',
        data
      );
    }

    if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') {
      // eslint-disable-next-line no-console
      console.log(
        '[WA_EMBEDDED_SIGNUP][listener] Evento ignorado, type no es WA_EMBEDDED_SIGNUP',
        payload && payload.type
      );
      return;
    }

    // eslint-disable-next-line no-console
    console.log(
      '[WA_EMBEDDED_SIGNUP][listener] Payload WA_EMBEDDED_SIGNUP válido, llamando a onData',
      payload
    );

    onData(payload);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '[WA_EMBEDDED_SIGNUP][listener] Error procesando mensaje WA_EMBEDDED_SIGNUP',
      error
    );
  }
};

export const isValidBusinessData = data => {
  if (!data) return false;
  const {
    waba_id: wabaId,
    phone_number_id: phoneNumberId,
    business_id: businessId,
  } = data;

  /**
   * Formatos soportados:
   * - Flujo Cloud API clásico:
   *   data = { business_id, waba_id, phone_number_id, ... }
   * - Flujo WhatsApp Business App onboarding:
   *   data = { business_id, waba_id, ... } (NO incluye phone_number_id)
   *
   * Para el primer caso exigimos waba_id + phone_number_id.
   * Para el segundo caso aceptamos waba_id + business_id.
   */

  const hasCloudApiIdentifiers = Boolean(wabaId && phoneNumberId);
  const hasBusinessAppIdentifiers = Boolean(wabaId && businessId);

  return hasCloudApiIdentifiers || hasBusinessAppIdentifiers;
};


